/**
 * GLB-backed mob loader. Quaternius animals + monsters packs ship with
 * built-in walk/idle/attack animations. We pick a model per mob `kind`
 * from the server, parent it to a TransformNode the World scene can
 * position, and play loop animations based on movement state.
 *
 * Loader chain (R232q5am):
 *   1. Sirus M2-extracted GLB (sirus/creature/<name>/<name>.glb) —
 *      preferred when present. The chain HEAD-probes each `sirusPaths`
 *      candidate in order and uses the first 200-OK binary response.
 *   2. Sketchfab realistic / Quaternius pack GLB (def.path) —
 *      fallback that's always on disk.
 *
 * Caching: meshes are loaded once per model URL and re-used via
 * Babylon's clone() to avoid re-parsing the gltf for each spawn. URL
 * resolution (probe outcome) is cached per kind so subsequent spawns
 * skip the HEAD round-trip.
 */

import {
  type AnimationGroup,
  Color3,
  type Mesh,
  PBRMaterial,
  type Scene,
  SceneLoader,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { sanitizeGlb } from './glb-sanitizer.js';
import { lookupSirusCreaturePath } from './sirus-creature-paths.js';

const BASE = `${import.meta.env.BASE_URL}models/`;

interface MobModel {
  /** Path under /models/ — e.g. "animals/glTF/Wolf.gltf".
   *
   *  This is the FALLBACK (Quaternius / Sketchfab) — used only when none of
   *  `sirusPaths` HEAD-probes successfully. Always present on disk. */
  path: string;
  /** R232q5am: ordered list of Sirus M2-extracted GLBs to try BEFORE falling
   *  back to `path`. Paths are relative to /models/, e.g.
   *  'sirus/creature/wolf/wolf.glb'. The first that HEAD-probes 200 (with a
   *  binary content-type, not text/html) wins. Once known-good, the URL is
   *  cached so subsequent spawns skip the probe.
   *
   *  Why a list: Sirus has many variants per concept (wolf, wolf2,
   *  alliancewolfmount, bloodwolf, …) — listing the most "WotLK 3.3.5a-looking"
   *  first gives best aesthetic match. */
  sirusPaths?: string[];
  /** Multiplier vs. native scale to look right next to a 1.2× character. */
  scale: number;
  /** y-offset added so feet sit on the ground. */
  yOffset: number;
  /** R232q5dd: target height в metres. Если задано — bbox auto-normalize к
   *  этому числу (для Sketchfab realistic GLB которые приходят в native units
   *  и без нормализации летают / огромные). Если undefined — использовать
   *  raw `scale` без normalize (для Quaternius packs). */
  targetHeight?: number;
}

// R132: каждый mob.kind ↔ mesh должен быть осмысленно совмещён
// (фидбэк юзера: 'если это волк, называй его волком; не муху слоном').
// Где нет точного матча — переименовали kind в то, чем модель является
// (boar→bull, spider→cactoro). RU_MOB_KIND обновлён синхронно.
// R232q5am: Sirus M2-extracted creature paths (under
// /models/sirus/creature/<name>/<name>.glb). Each kind below has an ordered
// `sirusPaths` array — first to HEAD-probe 200 wins; if all 404 the existing
// Sketchfab/Quaternius `path` is used. Names match the Sirus MPQ folder
// convention (lowercase, no spaces).
const SIRUS = (name: string) => `sirus/creature/${name}/${name}.glb`;

/** Prod-probe telemetry: which URL each kind resolved to and how loads went. */
const MOBLOG = { resolved: {} as Record<string, string>, ok: {} as Record<string, number>, err: {} as Record<string, string>, stage: {} as Record<string, string> };
if (typeof window !== 'undefined') (window as unknown as { __aiwariMob?: typeof MOBLOG }).__aiwariMob = MOBLOG;

/**
 * R232q86zh6: canonical CreatureDisplayInfo lookup — displayId →
 * { m: 'wolf/wolf', s: ['wolfskintimber', ...], scale }. Built on the hive
 * from the original DBCs (scripts/dbc_display_map.py); ~140k entries, lazy
 * single fetch, gzip ~1.5 MB.
 */
interface DisplayRec { m: string; s: string[]; scale: number }
let displayMapPromise: Promise<Record<string, DisplayRec> | null> | null = null;
function getDisplayMap(): Promise<Record<string, DisplayRec> | null> {
  if (!displayMapPromise) {
    displayMapPromise = fetch(`${import.meta.env.BASE_URL}data/creature-display.json`, { cache: 'force-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return displayMapPromise;
}

/** HEAD-probe cache for display-resolved GLBs (name → exists). */
const DISPLAY_GLB_OK = new Map<string, Promise<boolean>>();
function probeCreatureGlb(name: string): Promise<boolean> {
  let p = DISPLAY_GLB_OK.get(name);
  if (!p) {
    p = fetch(`${BASE}sirus/creature/${name}/${name}.glb`, { method: 'HEAD' })
      .then((r) => r.ok && !(r.headers.get('content-type') ?? '').includes('text/html'))
      .catch(() => false);
    DISPLAY_GLB_OK.set(name, p);
  }
  return p;
}

/** Resolve displayId → { url, skin } against OUR converted model library. */
async function resolveByDisplayId(displayId: number): Promise<{ url: string; skin: string | null } | null> {
  if (!displayId) return null;
  const map = await getDisplayMap();
  const rec = map?.[String(displayId)];
  if (!rec) return null;
  const name = rec.m.split('/').pop() ?? '';
  if (!name) return null;
  if (!(await probeCreatureGlb(name))) return null;
  return { url: `${BASE}sirus/creature/${name}/${name}.glb`, skin: rec.s[0] ?? null };
}

const MOB_MODELS: Record<string, MobModel> = {
  // R232q5cz: realistic Sketchfab CC-BY rigs — приоритет для часто-видимых мобов.
  // R232q5am: Sirus paths added как primary, Sketchfab/Quaternius — fallback.
  wolf:        { sirusPaths: [SIRUS('wolf'), SIRUS('direwolf')], path: 'sketchfab/realistic/dire-wolf.glb', scale: 1.0, yOffset: 0, targetHeight: 1.10 },
  worg:        { sirusPaths: [SIRUS('wolf'), SIRUS('direwolf')], path: 'sketchfab/realistic/dire-wolf.glb', scale: 1.0, yOffset: 0, targetHeight: 1.40 },
  ghoul:       { sirusPaths: [SIRUS('ghoul'), SIRUS('zombie')], path: 'sketchfab/realistic/ghoul-undead.glb', scale: 1.0, yOffset: 0, targetHeight: 1.70 },
  zombie:      { sirusPaths: [SIRUS('zombie'), SIRUS('ghoul')], path: 'sketchfab/realistic/ghoul-undead.glb', scale: 1.0, yOffset: 0, targetHeight: 1.75 },
  death_knight: { sirusPaths: [SIRUS('deathknight'), SIRUS('lichking')], path: 'sketchfab/realistic/death-knight.glb', scale: 1.0, yOffset: 0, targetHeight: 2.10 },
  bandit_realistic: { sirusPaths: [SIRUS('humanmalebanditmedium'), SIRUS('humanmalewarriormedium')], path: 'sketchfab/realistic/armored-guard-knight-rig.glb', scale: 1.0, yOffset: 0, targetHeight: 1.85 },
  guard:       { sirusPaths: [SIRUS('humanmalewarriormedium'), SIRUS('humanmaleguardheavy')], path: 'sketchfab/realistic/armored-guard-knight-rig.glb', scale: 1.0, yOffset: 0, targetHeight: 1.85 },
  knight:      { sirusPaths: [SIRUS('humanmaleknightheavy'), SIRUS('humanmalewarriorheavy')], path: 'sketchfab/realistic/medieval-knight.glb', scale: 1.0, yOffset: 0, targetHeight: 1.90 },
  paladin_mob: { sirusPaths: [SIRUS('humanmalepaladinheavy'), SIRUS('humanmaleknightheavy')], path: 'sketchfab/realistic/medieval-knight.glb', scale: 1.0, yOffset: 0, targetHeight: 2.00 },
  skeleton:    { sirusPaths: [SIRUS('skeleton'), SIRUS('skeletonmage'), SIRUS('skeletalwarrior')], path: 'sketchfab/realistic/cursed-undead-soldier-rig.glb', scale: 1.0, yOffset: 0, targetHeight: 1.85 },
  skeleton_warrior: { sirusPaths: [SIRUS('skeletalwarrior'), SIRUS('skeleton')], path: 'sketchfab/realistic/cursed-undead-soldier-rig.glb', scale: 1.0, yOffset: 0, targetHeight: 1.95 },
  mage_realistic: { sirusPaths: [SIRUS('humanfemalemagecloth'), SIRUS('humanmalemagecloth')], path: 'sketchfab/realistic/elf-wizard.glb', scale: 1.0, yOffset: 0, targetHeight: 1.85 },
  wyvern:      { sirusPaths: [SIRUS('wyvern'), SIRUS('wyvernmount')], path: 'sketchfab/realistic/wyvern-mount.glb', scale: 1.0, yOffset: 0.6, targetHeight: 3.50 },

  // Animals — all have Sirus M2 equivalents.
  fox:         { sirusPaths: [SIRUS('fox'), SIRUS('foxsmall')], path: 'animals/glTF/Fox.gltf',          scale: 0.8, yOffset: 0 },
  deer:        { sirusPaths: [SIRUS('deer'), SIRUS('deerskinned')], path: 'animals/glTF/Deer.gltf',         scale: 1.0, yOffset: 0 },
  stag:        { sirusPaths: [SIRUS('stag'), SIRUS('deer')], path: 'animals/glTF/Stag.gltf',         scale: 1.1, yOffset: 0 },
  bull:        { sirusPaths: [SIRUS('cow'), SIRUS('bull'), SIRUS('cattle')], path: 'animals/glTF/Bull.gltf',         scale: 1.0, yOffset: 0 },
  cow:         { sirusPaths: [SIRUS('cow'), SIRUS('cattle')], path: 'animals/glTF/Cow.gltf',          scale: 1.0, yOffset: 0 },
  husky:       { sirusPaths: [SIRUS('dog'), SIRUS('worgenwolfmount')], path: 'animals/glTF/Husky.gltf',        scale: 0.9, yOffset: 0 },
  horse:       { sirusPaths: [SIRUS('horse'), SIRUS('horsesmall'), SIRUS('alliancerider')], path: 'animals/glTF/Horse.gltf',        scale: 1.1, yOffset: 0 },
  boar:        { sirusPaths: [SIRUS('boar'), SIRUS('boar2')], path: 'animals/glTF/Bull.gltf',         scale: 0.9, yOffset: 0 },
  spider:      { sirusPaths: [SIRUS('spider'), SIRUS('spider2')], path: 'monsters/Big/glTF/Cactoro.gltf', scale: 0.9, yOffset: 0 },

  // R232q5f / R232q5am — humanoid mobs.
  bandit:      { sirusPaths: [SIRUS('humanmalebanditmedium'), SIRUS('humanmalebanditlight')], path: 'sketchfab/realistic/armored-guard-knight-rig.glb', scale: 1.0, yOffset: 0, targetHeight: 1.85 },
  tribal:      { sirusPaths: [SIRUS('orcmalewarriorlight'), SIRUS('trollmalewarriorlight')], path: 'sketchfab/realistic/orc-warrior.glb', scale: 1.0, yOffset: 0, targetHeight: 1.95 },
  goblin:      { sirusPaths: [SIRUS('goblinmaleworker'), SIRUS('goblinmalewarrior')], path: 'monsters/Big/glTF/Orc.gltf',     scale: 1.0, yOffset: 0 },
  orc:         { sirusPaths: [SIRUS('orcmalewarriormedium'), SIRUS('orcmalewarriorheavy')], path: 'sketchfab/realistic/orc-warrior.glb', scale: 1.0, yOffset: 0, targetHeight: 2.05 },
  orc_skull:   { sirusPaths: [SIRUS('orcmalewarriorheavy')], path: 'monsters/Big/glTF/Orc_Skull.gltf', scale: 1.2, yOffset: 0 },
  yeti:        { sirusPaths: [SIRUS('yeti'), SIRUS('yetinorthrend')], path: 'monsters/Big/glTF/Yeti.gltf',    scale: 1.6, yOffset: 0 },
  golem:       { sirusPaths: [SIRUS('golem'), SIRUS('mountaingiant'), SIRUS('mountaingiantcrystal')], path: 'monsters/Big/glTF/Yeti.gltf',    scale: 1.6, yOffset: 0 },

  // Magical
  demon:       { sirusPaths: [SIRUS('felguard'), SIRUS('infernal'), SIRUS('felhunter')], path: 'monsters/Big/glTF/Demon.gltf',   scale: 1.3, yOffset: 0 },
  blue_demon:  { sirusPaths: [SIRUS('felhunter'), SIRUS('imp')], path: 'monsters/Big/glTF/BlueDemon.gltf', scale: 1.3, yOffset: 0 },
  mushroom:    { sirusPaths: [SIRUS('mushroom'), SIRUS('sporebat')], path: 'monsters/Big/glTF/MushroomKing.gltf', scale: 1.4, yOffset: 0 },
  cactoro:     { sirusPaths: [SIRUS('plantmonster'), SIRUS('treant')], path: 'monsters/Big/glTF/Cactoro.gltf', scale: 0.9, yOffset: 0 },
  dino:        { sirusPaths: [SIRUS('raptor'), SIRUS('devilsaur')], path: 'monsters/Big/glTF/Dino.gltf',    scale: 1.4, yOffset: 0 },
  alien:       { sirusPaths: [SIRUS('faceless'), SIRUS('eredar')], path: 'monsters/Big/glTF/Alien.gltf',   scale: 1.0, yOffset: 0 },

  // Flying / undead
  wraith:      { sirusPaths: [SIRUS('ghost'), SIRUS('wraith')], path: 'monsters/Flying/glTF/Ghost.gltf', scale: 1.2, yOffset: 0.4 },
  ghost_skull: { sirusPaths: [SIRUS('ghost'), SIRUS('skullghost')], path: 'monsters/Flying/glTF/Ghost_Skull.gltf', scale: 1.2, yOffset: 0.4 },
  dragonkin:   { sirusPaths: [SIRUS('dragon'), SIRUS('drake'), SIRUS('whelp')], path: 'monsters/Flying/glTF/Dragon.gltf', scale: 1.2, yOffset: 0.6 },
  dragon_elite: { sirusPaths: [SIRUS('alexstrasza'), SIRUS('onyxia'), SIRUS('dragon')], path: 'monsters/Flying/glTF/Dragon_Evolved.gltf', scale: 1.6, yOffset: 0.8 },
  'world-boss': { sirusPaths: [SIRUS('alexstrasza'), SIRUS('onyxia'), SIRUS('frostwurm')], path: 'monsters/Flying/glTF/Dragon_Evolved.gltf', scale: 3.0, yOffset: 1.5 },

  // R151: ещё WotLK 3.3.5a-style mobs (animals + monsters/Big + Flying).
  // R232q5am: Sirus paths added где есть очевидный аналог.
  alpaca:      { sirusPaths: [SIRUS('alpaca'), SIRUS('alpacamount')], path: 'animals/glTF/Alpaca.gltf',       scale: 0.9, yOffset: 0 },
  donkey:      { sirusPaths: [SIRUS('donkey'), SIRUS('mule')], path: 'animals/glTF/Donkey.gltf',       scale: 0.95, yOffset: 0 },
  horse_white: { sirusPaths: [SIRUS('horsewhite'), SIRUS('horse')], path: 'animals/glTF/Horse_White.gltf',  scale: 1.1, yOffset: 0 },
  shibainu:    { sirusPaths: [SIRUS('dog')], path: 'animals/glTF/ShibaInu.gltf',     scale: 0.7, yOffset: 0 },

  // Misc monsters (Quaternius):
  bunny:       { sirusPaths: [SIRUS('rabbit'), SIRUS('bunny')], path: 'monsters/Big/glTF/Bunny.gltf',   scale: 0.6, yOffset: 0 },
  birb:        { sirusPaths: [SIRUS('crow'), SIRUS('owl'), SIRUS('hawkstrider')], path: 'monsters/Big/glTF/Birb.gltf',    scale: 0.8, yOffset: 0 },
  frog:        { sirusPaths: [SIRUS('frog'), SIRUS('toad')], path: 'monsters/Big/glTF/Frog.gltf',    scale: 0.7, yOffset: 0 },
  fish:        { sirusPaths: [SIRUS('fish'), SIRUS('shark')], path: 'monsters/Big/glTF/Fish.gltf',    scale: 0.8, yOffset: 0.4 },
  monkroose:   { sirusPaths: [SIRUS('moose')], path: 'monsters/Big/glTF/Monkroose.gltf', scale: 1.1, yOffset: 0 },

  // Flying:
  alpaking:        { sirusPaths: [SIRUS('hippogryph'), SIRUS('hawkstrider')], path: 'monsters/Flying/glTF/Alpaking.gltf', scale: 1.3, yOffset: 0.3 },
  alpaking_elite:  { sirusPaths: [SIRUS('hippogryph'), SIRUS('phoenix')], path: 'monsters/Flying/glTF/Alpaking_Evolved.gltf', scale: 1.7, yOffset: 0.5 },
  armabee:         { sirusPaths: [SIRUS('wasp'), SIRUS('bee')], path: 'monsters/Flying/glTF/Armabee.gltf', scale: 0.9, yOffset: 0.4 },
  armabee_elite:   { sirusPaths: [SIRUS('wasp'), SIRUS('bee')], path: 'monsters/Flying/glTF/Armabee_Evolved.gltf', scale: 1.2, yOffset: 0.5 },
  demon_flying:    { sirusPaths: [SIRUS('felbat'), SIRUS('infernal')], path: 'monsters/Flying/glTF/Demon.gltf', scale: 1.3, yOffset: 0.5 },
};

export interface GlbMob {
  root: TransformNode;
  setMoving: (m: boolean) => void;
  triggerAttack: () => void;
  /** R228: пауза/возобновление anims для distance-cull. */
  setAnimEnabled?: (en: boolean) => void;
  dispose: () => void;
}

// R232q5f: master-mesh cache — load each unique GLB once, then clone() per
// mob spawn. Avoids 200×same-GLB fetches that OOM'd Chrome. Loaded meshes
// are kept disabled at world origin so they don't render; clones inherit
// geometry buffers via instancing.
// R232q86zh1: the cache now holds an AssetContainer (kept OUT of the scene).
// Each spawn calls instantiateModelsToScene → every mob gets its OWN skeleton
// with animation groups retargeted onto it. The old hand-rolled mesh.clone()
// shared the master skeleton, so every wolf stepped in the same phase and
// per-mob animation control was impossible.
type CachedModel = import('@babylonjs/core').AssetContainer;
const MODEL_CACHE = new Map<string, Promise<CachedModel>>();

/**
 * Call this when the Babylon scene is disposed (World.tsx useEffect cleanup).
 * Clears master-mesh cache so the next scene gets fresh meshes — avoids
 * cloning from masters that belong to the old disposed scene (double GPU alloc
 * → OOM). HEAD_CACHE and RESOLVED_URL stay alive: URL resolution is scene-
 * independent and re-probing would just waste network round-trips.
 */
export function clearGlbMobCache(): void {
  // Dispose master meshes so their GPU buffers are freed.
  for (const p of MODEL_CACHE.values()) {
    p.then((m) => {
      for (const mesh of m.meshes) {
        try { mesh.dispose(false, true); } catch { /* already gone */ }
      }
      for (const ag of m.animationGroups) {
        try { ag.dispose(); } catch {}
      }
    }).catch(() => {});
  }
  MODEL_CACHE.clear();
}

// R232q5am: cached HEAD-probe results so we ask once per Sirus URL. true =
// 200 + binary content-type, false = 404 / non-binary / fetch failure.
const HEAD_CACHE = new Map<string, Promise<boolean>>();
function probeUrl(url: string): Promise<boolean> {
  let p = HEAD_CACHE.get(url);
  if (p) return p;
  p = (async () => {
    try {
      const r = await fetch(url, { method: 'HEAD' });
      if (!r.ok) return false;
      const ct = (r.headers.get('content-type') ?? '').toLowerCase();
      // SPA hosts often redirect 404→200(index.html). Reject text/html.
      if (ct.includes('text/html')) return false;
      return true;
    } catch {
      return false;
    }
  })();
  HEAD_CACHE.set(url, p);
  return p;
}

// R232q5am: per-kind resolved URL cache. Once we know which Sirus path (or
// fallback) is the right one for a kind, every subsequent spawn skips the
// probe loop. Key = kind, value = absolute URL (already includes BASE).
const RESOLVED_URL = new Map<string, Promise<string>>();
function resolveMobUrl(def: MobModel, kind: string): Promise<string> {
  let p = RESOLVED_URL.get(kind);
  if (p) return p;
  p = (async () => {
    // R232q43: build the union of Sirus candidates: MOB_MODELS.sirusPaths
    // (curated, highest priority) followed by lookupSirusCreaturePath()
    // results from the 1.8k-creature library. Dedup so we don't probe the
    // same URL twice.
    const candidates: string[] = [];
    const seen = new Set<string>();
    const pushCandidate = (rel: string) => {
      if (!seen.has(rel)) { seen.add(rel); candidates.push(rel); }
    };
    if (def.sirusPaths) for (const r of def.sirusPaths) pushCandidate(r);
    const fuzzy = lookupSirusCreaturePath(kind);
    if (fuzzy) for (const r of fuzzy) pushCandidate(r);

    if (candidates.length > 0) {
      // Probe all candidates in parallel — sequential probes blocked mob
      // spawns by 1 network round-trip per candidate before the fallback
      // Quaternius GLB was even fetched.
      const probes = candidates.map((rel) => ({ rel, url: BASE + rel }));
      const results = await Promise.all(
        probes.map(async ({ rel, url }) => ({ rel, url, ok: await probeUrl(url) }))
      );
      // Pick the first that succeeded, preserving order preference.
      const winner = results.find((r) => r.ok);
      if (winner) {
        // R232q43: Player-facing log uses 'AIWARI mesh' wording — no
        // upstream-asset references in console.
        console.log(`[mob] ${kind}: using AIWARI mesh ${winner.rel.split('/').pop()}`);
        return winner.url;
      }
    }
    // Fallback to Quaternius/Sketchfab.
    return BASE + def.path;
  })();
  RESOLVED_URL.set(kind, p);
  return p;
}

// R232q5f: concurrent-load semaphore. Limits to 3 simultaneous GLB fetches
// so the browser doesn't lock up parsing 20MB GLBs in parallel.
const LOAD_QUEUE: Array<() => void> = [];
let LOADING_NOW = 0;
const MAX_CONCURRENT = 3;

function acquireLoadSlot(): Promise<void> {
  return new Promise((resolve) => {
    if (LOADING_NOW < MAX_CONCURRENT) {
      LOADING_NOW++;
      resolve();
    } else {
      LOAD_QUEUE.push(() => { LOADING_NOW++; resolve(); });
    }
  });
}
function releaseLoadSlot() {
  LOADING_NOW--;
  const next = LOAD_QUEUE.shift();
  if (next) next();
}

/**
 * R232q5av: ensure mob materials never render as pure white / pure black.
 *
 * Problem: Quaternius animal GLBs (Bull.gltf and friends) ship PBR
 * materials with very low baseColorFactor (~0.027) and zero emissive.
 * Combined with high environmentIntensity + bright sun, the dark fur
 * either bleaches out under the rim light or — if the HDR fails to
 * load — turns pitch black with no diffuse signal. User reported
 * «Бык Ур.4 mob белый/блёклый».
 *
 * Fix: lift baseColorFactor below 0.15 to a minimum readable luma,
 * and add a faint emissive so the mob always reads on screen.
 */
function brightenMobMaterials(meshes: Mesh[]): void {
  const seen = new Set<unknown>();
  for (const mesh of meshes) {
    const mat = mesh.material;
    if (!mat || seen.has(mat)) continue;
    seen.add(mat);
    if (mat instanceof PBRMaterial) {
      // Lift very-dark base colours so animals don't render as black/white blobs.
      const c = mat.albedoColor;
      if (c) {
        const luma = c.r * 0.299 + c.g * 0.587 + c.b * 0.114;
        if (luma < 0.18) {
          const k = 0.18 / Math.max(luma, 0.001);
          mat.albedoColor = new Color3(
            Math.min(1, c.r * k),
            Math.min(1, c.g * k),
            Math.min(1, c.b * k),
          );
        }
      }
      // Slight emissive so mobs read in shadowed valleys.
      if (!mat.emissiveColor || (mat.emissiveColor.r + mat.emissiveColor.g + mat.emissiveColor.b) < 0.05) {
        mat.emissiveColor = new Color3(0.10, 0.10, 0.10);
        mat.emissiveIntensity = 1;
      }
      // Keep specular/metallic from blowing out under the rim light.
      if (mat.metallic === undefined || mat.metallic === null) mat.metallic = 0;
      if (mat.roughness == null || mat.roughness < 0.5) mat.roughness = 0.65;
    } else if (mat instanceof StandardMaterial) {
      const c = mat.diffuseColor;
      if (c) {
        const luma = c.r * 0.299 + c.g * 0.587 + c.b * 0.114;
        if (luma < 0.18) {
          const k = 0.18 / Math.max(luma, 0.001);
          mat.diffuseColor = new Color3(
            Math.min(1, c.r * k),
            Math.min(1, c.g * k),
            Math.min(1, c.b * k),
          );
        }
      }
      if (mat.emissiveColor.r + mat.emissiveColor.g + mat.emissiveColor.b < 0.05) {
        mat.emissiveColor = new Color3(0.10, 0.10, 0.10);
      }
      mat.specularColor = new Color3(0.05, 0.05, 0.05);
    }
  }
}

async function getCachedModel(scene: Scene, url: string, skinOverride?: string | null): Promise<CachedModel> {
  const cacheKey = skinOverride ? `${url}|${skinOverride}` : url;
  let p = MODEL_CACHE.get(cacheKey);
  if (p) return p;
  p = (async () => {
    const tag = url.split('/').pop() ?? url;
    MOBLOG.stage[tag] = 'queued';
    await acquireLoadSlot();
    MOBLOG.stage[tag] = 'sanitizing';
    let blobUrl: string | null = null;
    try {
      // Sanitize in-flight: Sirus creature GLBs have the same duplicate
      // skeleton_root issue as character GLBs. For Quaternius/Sketchfab GLBs
      // the sanitizer is a fast no-op (multiParented.length === 0).
      const sanitised = await sanitizeGlb(url);
      blobUrl = sanitised.blobUrl;
      MOBLOG.stage[tag] = 'importing';
      // NB: blob URLs carry no extension — without the explicit '.glb'
      // pluginExtension the loader resolves no plugin («loadAssets of
      // undefined») and every mob stayed an invisible placeholder.
      const container = await SceneLoader.LoadAssetContainerAsync('', blobUrl, scene, undefined, '.glb');
      MOBLOG.stage[tag] = 'imported';
      // Container assets never join the scene themselves — no -10000 parking
      // hacks needed; instantiateModelsToScene() spawns per-mob copies.
      brightenMobMaterials(container.meshes as Mesh[]);
      // 21.07: pywowlib GLBs carry no materials — Sirus creatures rendered as
      // black silhouettes. The pipeline drops a sibling <name>.png skin next
      // to each <name>.glb; bind it when the mesh has no textured material.
      if (/sirus\/creature\//.test(url)) {
        // Canonical skin from CreatureDisplayInfo when provided; else the
        // pipeline's default sibling <name>.png.
        const skinUrl = skinOverride
          ? url.replace(/[^/]+\.glb$/i, `${skinOverride}.png`)
          : url.replace(/\.glb$/i, '.png');
        const skinTex = new (await import('@babylonjs/core')).Texture(
          skinUrl, scene, false, false, undefined, null, () => { /* 404 = keep silhouette */ });
        for (const m of container.meshes) {
          const mat = m.material;
          const hasTex = !!(mat && (
            (mat instanceof PBRMaterial && mat.albedoTexture) ||
            (mat instanceof StandardMaterial && mat.diffuseTexture)));
          if (hasTex) continue;
          const sm = new StandardMaterial(`${m.name}-skin`, scene);
          sm.diffuseTexture = skinTex;
          sm.emissiveColor = new Color3(0.22, 0.22, 0.22);
          sm.specularColor = new Color3(0.04, 0.04, 0.04);
          sm.backFaceCulling = false;
          m.material = sm;
        }
      }
      return container;
    } finally {
      releaseLoadSlot();
      // Defer URL revocation 3 render frames — same as RealisticCharacter —
      // to give Babylon time to upload textures to GPU before the Blob dies.
      if (blobUrl) {
        const urlToRevoke = blobUrl;
        let frameCount = 0;
        const obs = scene.onAfterRenderObservable.add(() => {
          frameCount++;
          if (frameCount >= 3) {
            scene.onAfterRenderObservable.remove(obs);
            try { URL.revokeObjectURL(urlToRevoke); } catch { /* nop */ }
          }
        });
        void obs;
      }
    }
  })();
  MODEL_CACHE.set(cacheKey, p);
  // A failed load must not poison the cache forever — drop it so the next
  // spawn retries (e.g. transient network reset during cold start).
  p.catch(() => { if (MODEL_CACHE.get(cacheKey) === p) MODEL_CACHE.delete(cacheKey); });
  return p;
}

/**
 * R232q5h T3.4: per-anim-group refcount so a single mob calling
 * setAnimEnabled(false) doesn't freeze every other clone of the same kind
 * (they all share the master AnimationGroup). We only stop the group
 * when every clone has asked for it.
 */
const ANIM_GROUP_REF = new WeakMap<AnimationGroup, number>();
function refAnim(g: AnimationGroup): void {
  ANIM_GROUP_REF.set(g, (ANIM_GROUP_REF.get(g) ?? 0) + 1);
  if (!g.isPlaying) { try { g.play(true); } catch {} }
}
function unrefAnim(g: AnimationGroup): void {
  const c = (ANIM_GROUP_REF.get(g) ?? 1) - 1;
  if (c <= 0) {
    ANIM_GROUP_REF.delete(g);
    try { g.stop(); } catch {}
  } else {
    ANIM_GROUP_REF.set(g, c);
  }
}

export async function loadGlbMob(scene: Scene, kind: string, displayId = 0): Promise<GlbMob | null> {
  // R232q43: when kind isn't curated in MOB_MODELS, build a synthetic def
  // that uses Sirus fuzzy lookup as the primary source and the wolf model
  // as the visual fallback. This means every server-emitted mob.kind has a
  // chance at finding a matching mesh from the 1.8k-strong creature library
  // before degrading to the generic wolf.
  let def = MOB_MODELS[kind];
  if (!def) {
    const fuzzy = lookupSirusCreaturePath(kind);
    if (fuzzy && fuzzy.length > 0) {
      const wolfDef = MOB_MODELS.wolf!;
      def = {
        path: wolfDef.path,
        sirusPaths: fuzzy,
        scale: wolfDef.scale,
        yOffset: wolfDef.yOffset,
        targetHeight: wolfDef.targetHeight,
      };
    } else {
      def = MOB_MODELS.wolf!;
    }
  }
  if (!def) return null;
  // R232q86zh6: canonical DisplayInfo path first — the EXACT model+skin the
  // original game uses for this creature. Falls back to kind-based curation.
  let url: string;
  let skin: string | null = null;
  let displayScale = 1;
  const byDisplay = await resolveByDisplayId(displayId);
  if (byDisplay) {
    url = byDisplay.url;
    skin = byDisplay.skin;
    const map = await getDisplayMap();
    displayScale = map?.[String(displayId)]?.scale ?? 1;
    MOBLOG.resolved[kind] = `did:${displayId}→${url.slice(-40)}`;
  } else {
    // R232q5am: probe Sirus paths; fall back to def.path. URL cached per kind.
    url = await resolveMobUrl(def, kind);
    MOBLOG.resolved[kind] = url.slice(-60);
  }
  let result: { meshes: Mesh[]; animationGroups: AnimationGroup[] };
  try {
    const container = await getCachedModel(scene, url, skin);
    // R232q86zh1: real per-mob instantiation — clones the skeleton and
    // retargets every AnimationGroup onto it, so mobs animate independently
    // (the shared-master-skeleton era made whole packs move in lockstep).
    const suffix = Math.random().toString(36).slice(2, 8);
    const entries = container.instantiateModelsToScene(
      (name) => `${kind}-${suffix}-${name}`,
      false,
      { doNotInstantiate: true },
    );
    result = { meshes: entries.rootNodes as Mesh[], animationGroups: entries.animationGroups };
    MOBLOG.ok[kind] = (MOBLOG.ok[kind] ?? 0) + 1;
  } catch (e) {
    console.warn(`[aiwari] mob load failed for ${kind}:`, (e as Error).message);
    MOBLOG.err[kind] = String((e as Error).message ?? e).slice(0, 120);
    return null;
  }
  const root = new TransformNode(`mob-glb-${kind}`, scene);
  const inner = new TransformNode(`mob-glb-${kind}-inner`, scene);
  inner.parent = root;
  inner.rotation.y = Math.PI; // match facing convention
  inner.position.y = def.yOffset;
  for (const m of result.meshes) {
    if (!m.parent) m.parent = inner;
    // Mobs receive click hits — propagate metadata via the root only.
    void m;
  }
  // R232q5dd: bbox auto-normalize для realistic Sketchfab GLBs (dire-wolf,
  // ghoul, knight). Без этого мобы летают в воздухе или огромные. Quaternius
  // packs остаются на raw scale (def.targetHeight=undefined).
  let finalScale = def.scale;
  if (def.targetHeight && def.targetHeight > 0) {
    // R232q5az M07: bbox calc must descend into sub-meshes — top-level clones
    // are TransformNodes/Meshes wrapping geometry-bearing children.
    const allClones: Mesh[] = [];
    for (const top of result.meshes) {
      allClones.push(top);
      try {
        for (const c of top.getChildMeshes(false)) allClones.push(c as Mesh);
      } catch {}
    }
    let bboxTallest = 0;
    for (const m of allClones) {
      if (!(m as Mesh).getTotalVertices || (m as Mesh).getTotalVertices() === 0) continue;
      (m as Mesh).refreshBoundingInfo();
      const bb = (m as Mesh).getBoundingInfo().boundingBox;
      const t = Math.max(
        bb.maximumWorld.x - bb.minimumWorld.x,
        bb.maximumWorld.y - bb.minimumWorld.y,
        bb.maximumWorld.z - bb.minimumWorld.z,
      );
      if (t > bboxTallest) bboxTallest = t;
    }
    if (bboxTallest > 0.001) {
      const autoScale = def.targetHeight / bboxTallest;
      finalScale = autoScale * def.scale;
      console.log(`[mob-glb] ${kind}: bbox=${bboxTallest.toFixed(2)} target=${def.targetHeight} autoScale=${autoScale.toFixed(3)} final=${finalScale.toFixed(3)}`);
    }
  }
  finalScale *= displayScale; // CreatureDisplayInfo per-display scale
  root.scaling = new Vector3(finalScale, finalScale, finalScale);

  // Pick animations.
  const anims = result.animationGroups;
  // Instantiated groups carry the `${kind}-${suffix}-` prefix from the namer.
  const find = (name: string) => anims.find((g) => g.name === name || g.name.endsWith(`-${name}`));
  // R232q86zh1: Sirus M2 exports name groups `anim_<m2SequenceId>_<variation>`
  // (0=Stand, 1=Death, 4=Walk, 5=Run, 16/17/18=Attack). The old matcher only
  // knew Quaternius names ('Idle'/'Walk') → NOTHING played and every Sirus
  // creature slid around in bind pose («пластиковость»).
  const byId = (id: number) =>
    anims.find((g) => new RegExp(`(^|-)anim_${id}(_|$)`).test(g.name)) ?? null;
  // Quaternius animals: Idle, Walk, Run, Attack, Death, Eating, Idle_Eating, Jump.
  // Monsters: Idle, Walk, Run, Attack, Death.
  const idle = find('Idle') ?? find('Idle_A') ?? byId(0);
  // Wander uses walk speed — prefer the WALK sequence (4); run (5) is the
  // chase fallback until setMoving learns a `running` flag.
  const walk = byId(4) ?? byId(5) ?? find('Walk') ?? find('Walk_A');
  const attack = find('Attack') ?? find('Attack_A') ?? find('Bite_Attack') ?? byId(16) ?? byId(17) ?? byId(18);

  // R232q5h T3.4: do NOT g.stop() at boot — that wipes refcount for clones
  // already alive. Just pick the starting anim.
  let current: AnimationGroup | null = null;
  let attackPlaying = false;
  let myAnimEnabled = true;
  function play(g: AnimationGroup | null | undefined) {
    if (current === g || !g) return;
    if (current && myAnimEnabled) unrefAnim(current);
    g.weight = 1;
    if (myAnimEnabled) refAnim(g);
    current = g;
  }
  if (idle) play(idle);

  return {
    root,
    setMoving: (m) => {
      if (attackPlaying) return;
      play(m ? (walk ?? idle) : idle);
    },
    triggerAttack: () => {
      if (!attack || attackPlaying) return;
      attackPlaying = true;
      const prev = current;
      const cb = () => {
        attackPlaying = false;
        attack.onAnimationGroupEndObservable.removeCallback(cb);
        if (prev) play(prev);
        else if (idle) play(idle);
      };
      attack.onAnimationGroupEndObservable.add(cb);
      if (current) current.stop();
      current = null;
      attack.weight = 1;
      attack.play(false);
    },
    setAnimEnabled: (en: boolean) => {
      // R232q5h T3.4: refcount per group so this clone's pause doesn't
      // freeze every other clone sharing the same master AnimationGroup.
      if (en === myAnimEnabled) return;
      if (en) {
        myAnimEnabled = true;
        if (current) refAnim(current);
        else if (idle) { current = idle; refAnim(idle); }
      } else {
        myAnimEnabled = false;
        if (current) unrefAnim(current);
      }
    },
    dispose: () => {
      // R232q5h T3.4: only release this clone's refs — never dispose the
      // master AnimationGroup (shared by every clone of this kind).
      if (myAnimEnabled && current) unrefAnim(current);
      for (const m of result.meshes) (m as Mesh).dispose(false, true);
      inner.dispose();
      root.dispose(false, true);
    },
  };
}
